/**
 * ═══════════════════════════════════════════════════════════════════════════
 * useAIEndpoints - WASP Frontend v2.2.0
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @file useAIEndpoints.ts
 * @version 1.0.0
 * @description Hook para gestión CRUD de endpoints de IA
 * @author WASP Development Team
 * 
 * ENDPOINTS BACKEND:
 * - GET    /api/ai-endpoints
 * - GET    /api/ai-endpoints/:id
 * - GET    /api/ai-endpoints/capability/:capability
 * - POST   /api/ai-endpoints
 * - PUT    /api/ai-endpoints/:id
 * - DELETE /api/ai-endpoints/:id
 * - POST   /api/ai-endpoints/:id/toggle
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  AIEndpoint,
  AIEndpointFormData,
  AIEndpointListResponse,
  AIEndpointResponse,
  AIEndpointCreateResponse,
  AIEndpointUpdateResponse,
  AIEndpointDeleteResponse,
  AICapability,
  AIEndpointFilters
} from '../types/ai.types';

// ═══════════════════════════════════════════════════════════════════════════
// CONFIGURACIÓN
// ═══════════════════════════════════════════════════════════════════════════

const API_BASE_URL = 'https://backend.anubisai.net';
const API_KEY = 'anubis-whatsapp-api-key';

// Helper para obtener token (igual que en otros componentes WASP)
const getHatateToken = () => {
  return localStorage.getItem('hatateToken') || '';
};

// Helper para headers
const getHeaders = () => ({
  'Authorization': 'Bearer ' + getHatateToken(),
  'X-API-Key': API_KEY,
  'Content-Type': 'application/json'
});

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

interface UseAIEndpointsReturn {
  // Estado
  endpoints: AIEndpoint[];
  isLoading: boolean;
  error: string | null;
  
  // Métodos CRUD
  fetchEndpoints: () => Promise<void>;
  fetchEndpointById: (id: string) => Promise<AIEndpoint | null>;
  fetchEndpointsByCapability: (capability: AICapability) => Promise<AIEndpoint[]>;
  createEndpoint: (data: AIEndpointFormData) => Promise<AIEndpoint | null>;
  updateEndpoint: (id: string, data: Partial<AIEndpointFormData>) => Promise<AIEndpoint | null>;
  deleteEndpoint: (id: string) => Promise<boolean>;
  toggleEndpoint: (id: string, enabled: boolean) => Promise<AIEndpoint | null>;
  
  // Utilidades
  refreshEndpoints: () => Promise<void>;
  getEndpointById: (id: string) => AIEndpoint | undefined;
  filterEndpoints: (filters: AIEndpointFilters) => AIEndpoint[];
}

// ═══════════════════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════════════════

export const useAIEndpoints = (): UseAIEndpointsReturn => {
  // ───────────────────────────────────────────────────────────────────────
  // Estado
  // ───────────────────────────────────────────────────────────────────────
  
  const [endpoints, setEndpoints] = useState<AIEndpoint[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // ───────────────────────────────────────────────────────────────────────
  // Fetch: Listar todos los endpoints
  // ───────────────────────────────────────────────────────────────────────
  
  const fetchEndpoints = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await axios.get<AIEndpointListResponse>(
        `${API_BASE_URL}/api/whatsapp/wasp/ai-endpoints`,
        { headers: getHeaders() }
      );
      
      if (response.data.success) {
        setEndpoints(response.data.endpoints);
        console.log('[useAIEndpoints] Endpoints loaded:', response.data.count);
      } else {
        throw new Error('Failed to fetch endpoints');
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message || 'Error loading endpoints';
      setError(errorMsg);
      console.error('[useAIEndpoints] Error fetching endpoints:', errorMsg);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ───────────────────────────────────────────────────────────────────────
  // Fetch: Obtener endpoint por ID
  // ───────────────────────────────────────────────────────────────────────
  
  const fetchEndpointById = useCallback(async (id: string): Promise<AIEndpoint | null> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await axios.get<AIEndpointResponse>(
        `${API_BASE_URL}/api/whatsapp/wasp/ai-endpoints/${id}`,
        { headers: getHeaders() }
      );
      
      if (response.data.success) {
        console.log('[useAIEndpoints] Endpoint fetched:', id);
        return response.data.endpoint;
      } else {
        throw new Error('Endpoint not found');
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message || 'Error loading endpoint';
      setError(errorMsg);
      console.error('[useAIEndpoints] Error fetching endpoint:', errorMsg);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ───────────────────────────────────────────────────────────────────────
  // Fetch: Obtener endpoints por capacidad
  // ───────────────────────────────────────────────────────────────────────
  
  const fetchEndpointsByCapability = useCallback(async (capability: AICapability): Promise<AIEndpoint[]> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await axios.get<AIEndpointListResponse>(
        `${API_BASE_URL}/api/whatsapp/wasp/ai-endpoints/capability/${capability}`,
        { headers: getHeaders() }
      );
      
      if (response.data.success) {
        console.log('[useAIEndpoints] Endpoints by capability loaded:', response.data.count);
        return response.data.endpoints;
      } else {
        throw new Error('Failed to fetch endpoints by capability');
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message || 'Error loading endpoints';
      setError(errorMsg);
      console.error('[useAIEndpoints] Error fetching by capability:', errorMsg);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ───────────────────────────────────────────────────────────────────────
  // Create: Crear nuevo endpoint
  // ───────────────────────────────────────────────────────────────────────
  
  const createEndpoint = useCallback(async (data: AIEndpointFormData): Promise<AIEndpoint | null> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await axios.post<AIEndpointCreateResponse>(
        `${API_BASE_URL}/api/whatsapp/wasp/ai-endpoints`,
        data,
        { headers: getHeaders() }
      );
      
      if (response.data.success) {
        const newEndpoint = response.data.endpoint;
        setEndpoints(prev => [...prev, newEndpoint]);
        console.log('[useAIEndpoints] Endpoint created:', newEndpoint.id);
        return newEndpoint;
      } else {
        throw new Error(response.data.message || 'Failed to create endpoint');
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message || 'Error creating endpoint';
      setError(errorMsg);
      console.error('[useAIEndpoints] Error creating endpoint:', errorMsg);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ───────────────────────────────────────────────────────────────────────
  // Update: Actualizar endpoint existente
  // ───────────────────────────────────────────────────────────────────────
  
  const updateEndpoint = useCallback(async (
    id: string, 
    data: Partial<AIEndpointFormData>
  ): Promise<AIEndpoint | null> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await axios.put<AIEndpointUpdateResponse>(
        `${API_BASE_URL}/api/whatsapp/wasp/ai-endpoints/${id}`,
        data,
        { headers: getHeaders() }
      );
      
      if (response.data.success) {
        const updatedEndpoint = response.data.endpoint;
        setEndpoints(prev => 
          prev.map(ep => ep.id === id ? updatedEndpoint : ep)
        );
        console.log('[useAIEndpoints] Endpoint updated:', id);
        return updatedEndpoint;
      } else {
        throw new Error(response.data.message || 'Failed to update endpoint');
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message || 'Error updating endpoint';
      setError(errorMsg);
      console.error('[useAIEndpoints] Error updating endpoint:', errorMsg);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ───────────────────────────────────────────────────────────────────────
  // Delete: Eliminar endpoint
  // ───────────────────────────────────────────────────────────────────────
  
  const deleteEndpoint = useCallback(async (id: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await axios.delete<AIEndpointDeleteResponse>(
        `${API_BASE_URL}/api/whatsapp/wasp/ai-endpoints/${id}`,
        { headers: getHeaders() }
      );
      
      if (response.data.success) {
        setEndpoints(prev => prev.filter(ep => ep.id !== id));
        console.log('[useAIEndpoints] Endpoint deleted:', id);
        return true;
      } else {
        throw new Error(response.data.message || 'Failed to delete endpoint');
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message || 'Error deleting endpoint';
      setError(errorMsg);
      console.error('[useAIEndpoints] Error deleting endpoint:', errorMsg);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ───────────────────────────────────────────────────────────────────────
  // Toggle: Activar/Desactivar endpoint
  // ───────────────────────────────────────────────────────────────────────
  
  const toggleEndpoint = useCallback(async (
    id: string, 
    enabled: boolean
  ): Promise<AIEndpoint | null> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await axios.post<AIEndpointUpdateResponse>(
        `${API_BASE_URL}/api/whatsapp/wasp/ai-endpoints/${id}/toggle`,
        { enabled },
        { headers: getHeaders() }
      );
      
      if (response.data.success) {
        const updatedEndpoint = response.data.endpoint;
        setEndpoints(prev => 
          prev.map(ep => ep.id === id ? updatedEndpoint : ep)
        );
        console.log('[useAIEndpoints] Endpoint toggled:', id, enabled);
        return updatedEndpoint;
      } else {
        throw new Error(response.data.message || 'Failed to toggle endpoint');
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message || 'Error toggling endpoint';
      setError(errorMsg);
      console.error('[useAIEndpoints] Error toggling endpoint:', errorMsg);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ───────────────────────────────────────────────────────────────────────
  // Refresh: Refrescar lista de endpoints
  // ───────────────────────────────────────────────────────────────────────
  
  const refreshEndpoints = useCallback(async () => {
    await fetchEndpoints();
  }, [fetchEndpoints]);

  // ───────────────────────────────────────────────────────────────────────
  // Get: Obtener endpoint del estado local por ID
  // ───────────────────────────────────────────────────────────────────────
  
  const getEndpointById = useCallback((id: string): AIEndpoint | undefined => {
    return endpoints.find(ep => ep.id === id);
  }, [endpoints]);

  // ───────────────────────────────────────────────────────────────────────
  // Filter: Filtrar endpoints localmente
  // ───────────────────────────────────────────────────────────────────────
  
  const filterEndpoints = useCallback((filters: AIEndpointFilters): AIEndpoint[] => {
    let filtered = [...endpoints];
    
    // Filtrar por capacidad
    if (filters.capability) {
      filtered = filtered.filter(ep => 
        ep.capabilities.includes(filters.capability!)
      );
    }
    
    // Filtrar por estado (enabled/disabled)
    if (filters.enabled !== undefined) {
      filtered = filtered.filter(ep => ep.enabled === filters.enabled);
    }
    
    // Filtrar por tipo
    if (filters.type) {
      filtered = filtered.filter(ep => 
        ep.type.toLowerCase() === filters.type!.toLowerCase()
      );
    }
    
    // Filtrar por búsqueda (nombre)
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(ep => 
        ep.name.toLowerCase().includes(searchLower) ||
        ep.type.toLowerCase().includes(searchLower) ||
        ep.url.toLowerCase().includes(searchLower)
      );
    }
    
    return filtered;
  }, [endpoints]);

  // ───────────────────────────────────────────────────────────────────────
  // Effect: Cargar endpoints al montar
  // ───────────────────────────────────────────────────────────────────────
  
  useEffect(() => {
    fetchEndpoints();
  }, [fetchEndpoints]);

  // ───────────────────────────────────────────────────────────────────────
  // Return
  // ───────────────────────────────────────────────────────────────────────
  
  return {
    // Estado
    endpoints,
    isLoading,
    error,
    
    // Métodos CRUD
    fetchEndpoints,
    fetchEndpointById,
    fetchEndpointsByCapability,
    createEndpoint,
    updateEndpoint,
    deleteEndpoint,
    toggleEndpoint,
    
    // Utilidades
    refreshEndpoints,
    getEndpointById,
    filterEndpoints
  };
};

// ═══════════════════════════════════════════════════════════════════════════
// FIN DEL ARCHIVO
// ═══════════════════════════════════════════════════════════════════════════